import { supabaseAEC as supabase } from './supabaseAEC';

/**
 * Built Environment Digital Delivery Intelligence
 * Supabase RPC client — graph traversal queries.
 */

/**
 * B1 — Tools relevant to a role at a specific project stage.
 * @param {string} roleSlug  e.g. "architect"
 * @param {string} stageSlug e.g. "design-development"
 * @returns {Promise<Array<{ tool_id, tool_name, tool_slug, avg_confidence, match_path }>>}
 */
export async function getToolsByRoleStage(roleSlug, stageSlug) {
  const { data, error } = await supabase.rpc('get_tools_by_role_stage', {
    p_role_slug:  roleSlug,
    p_stage_slug: stageSlug,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * B2 — Use-case tasks active at a project stage.
 * @param {string} stageSlug e.g. "construction-documents"
 * @returns {Promise<Array<{ task_id, task_name, task_slug }>>}
 */
export async function getTasksByStage(stageSlug) {
  const { data, error } = await supabase.rpc('get_tasks_by_stage', {
    p_stage_slug: stageSlug,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * B3 — Disciplines active at a project stage (with task counts).
 * @param {string} stageSlug e.g. "schematic-design"
 * @returns {Promise<Array<{ discipline_id, discipline_name, discipline_slug, task_count }>>}
 */
export async function getDisciplinesByStage(stageSlug) {
  const { data, error } = await supabase.rpc('get_disciplines_by_stage', {
    p_stage_slug: stageSlug,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * B4 — Tools mapped to a specific use-case task.
 * @param {string} taskSlug e.g. "clash-detection"
 * @returns {Promise<Array<{ tool_id, tool_name, tool_slug, confidence_score, source }>>}
 */
export async function getToolsByTask(taskSlug) {
  const { data, error } = await supabase.rpc('get_tools_by_task', {
    p_task_slug: taskSlug,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * B5 — Tools for a discipline + stage combination (ranked by confidence).
 * @param {string} disciplineSlug e.g. "structural"
 * @param {string} stageSlug      e.g. "construction"
 * @returns {Promise<Array<{ tool_id, tool_name, tool_slug, avg_confidence, task_count }>>}
 */
export async function getToolsByDisciplineStage(disciplineSlug, stageSlug) {
  const { data, error } = await supabase.rpc('get_tools_by_discipline_stage', {
    p_discipline_slug: disciplineSlug,
    p_stage_slug:      stageSlug,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch all project stages ordered by sort_order.
 * Used to populate stage selector UI.
 * @returns {Promise<Array<{ id, name, slug, sort_order }>>}
 */
export async function fetchProjectStages() {
  const { data, error } = await supabase
    .from('project_stage')
    .select('id, name, slug, sort_order')
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch all user roles.
 * Used to populate role selector UI.
 * @returns {Promise<Array<{ id, name, slug }>>}
 */
export async function fetchUserRoles() {
  const { data, error } = await supabase
    .from('user_role')
    .select('id, name, slug')
    .order('name', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/**
 * B8 — Tools for a role (no stage filter).
 * @param {string} roleSlug e.g. "architect"
 * @returns {Promise<Array<{ tool_id, tool_name, tool_slug, avg_confidence, task_count }>>}
 */
export async function getToolsByRole(roleSlug) {
  const { data, error } = await supabase.rpc('get_tools_by_role', {
    p_role_slug: roleSlug,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * B6 — Tools for a discipline (all stages).
 * @param {string} disciplineSlug e.g. "architecture"
 * @returns {Promise<Array<{ tool_id, tool_name, tool_slug, avg_confidence, task_count }>>}
 */
export async function getToolsByDiscipline(disciplineSlug) {
  const { data, error } = await supabase.rpc('get_tools_by_discipline', {
    p_discipline_slug: disciplineSlug,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * B7 — Tools for a stage (all disciplines).
 * @param {string} stageSlug e.g. "construction"
 * @returns {Promise<Array<{ tool_id, tool_name, tool_slug, avg_confidence, task_count }>>}
 */
export async function getToolsByStage(stageSlug) {
  const { data, error } = await supabase.rpc('get_tools_by_stage', {
    p_stage_slug: stageSlug,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch all active task use-cases.
 * @returns {Promise<Array<{ id, name, slug, description }>>}
 */
export async function fetchTaskUsecases() {
  const { data, error } = await supabase
    .from('task_usecase')
    .select('id, name, slug, description')
    .eq('is_active', true)
    .order('name', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch raw discipline_task rows (IDs only).
 * Caller maps IDs → slugs using already-loaded entity lists.
 * @returns {Promise<Array<{ discipline_id: string, task_id: string }>>}
 */
export async function fetchDisciplineTaskRaw() {
  const { data, error } = await supabase
    .from('discipline_task')
    .select('discipline_id, task_id');
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch raw stage_task rows (IDs only).
 * @returns {Promise<Array<{ stage_id: string, task_id: string }>>}
 */
export async function fetchStageTaskRaw() {
  const { data, error } = await supabase
    .from('stage_task')
    .select('stage_id, task_id');
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch raw task_role rows (IDs only).
 * @returns {Promise<Array<{ task_id: string, role_id: string }>>}
 */
export async function fetchRoleTaskRaw() {
  const { data, error } = await supabase
    .from('task_role')
    .select('task_id, role_id');
  if (error) throw error;
  return data ?? [];
}
